/**
 * F23 (Wave 2C) — admin "change commission rate" modal.
 *
 * Composes Modal + FormField + NumberInput (rendered as %, persisted as basis
 * points) + Textarea (reason 1–500) + forward-only Banner.
 *
 * Client-side validation via react-hook-form + Zod
 * (`SetVenueCommissionRateInputSchema`); 0–50% range mirrors the server
 * `[0, 5000]` basis-point cap. Submit calls
 * `adminCommissionApi.setVenueRate(venueId, { rateBasisPoints, reason })`.
 * 422 `COMMISSION_RATE_NO_CHANGE` surfaces inline as "Rate unchanged".
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, NumberInput, Textarea } from '@bravemove/ui-web';
import {
  MAX_RATE_BASIS_POINTS,
  MIN_RATE_BASIS_POINTS,
  SetVenueCommissionRateInputSchema,
  type SetVenueCommissionRateInput,
} from '@bravemove/types';

export interface EditRateDialogProps {
  readonly open: boolean;
  readonly venueId: string;
  readonly venueName: string;
  readonly currentRateBasisPoints: number;
  readonly onConfirm: (input: SetVenueCommissionRateInput) => Promise<void>;
  readonly onClose: () => void;
}

const MIN_PERCENT = MIN_RATE_BASIS_POINTS / 100; // 0
const MAX_PERCENT = MAX_RATE_BASIS_POINTS / 100; // 50

interface FormShape {
  ratePercent: number;
  reason: string;
}

function bpToPercent(bp: number): number {
  return Math.round((bp / 100) * 100) / 100;
}

function percentToBp(pct: number): number {
  // Round to integer basis points to defend against fp drift.
  return Math.round(pct * 100);
}

export function EditRateDialog({
  open,
  venueId,
  venueName,
  currentRateBasisPoints,
  onConfirm,
  onClose,
}: EditRateDialogProps) {
  const { t } = useI18n();
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
    reset,
  } = useForm<FormShape>({
    mode: 'onChange',
    defaultValues: {
      ratePercent: bpToPercent(currentRateBasisPoints),
      reason: '',
    },
  });

  useEffect(() => {
    if (!open) return;
    reset({ ratePercent: bpToPercent(currentRateBasisPoints), reason: '' });
    setServerError(null);
    setSubmitting(false);
  }, [open, currentRateBasisPoints, reset]);

  const ratePercent = watch('ratePercent');
  const reason = watch('reason');
  const rateBasisPoints = percentToBp(Number(ratePercent ?? 0));
  const unchanged = rateBasisPoints === currentRateBasisPoints;

  const submit = handleSubmit(async (data) => {
    setServerError(null);
    const candidate: SetVenueCommissionRateInput = {
      rateBasisPoints: percentToBp(Number(data.ratePercent)),
      reason: data.reason.trim(),
    };
    // Final guard via the shared Zod schema (the form `mode: 'onChange'` only
    // sanity-checks the form shape; this catches any stray bp/percent miss).
    const parsed = SetVenueCommissionRateInputSchema.safeParse(candidate);
    if (!parsed.success) {
      setServerError(
        t('admin:commission.dialog.edit_rate.error.out_of_bounds', {
          defaultValue: 'Rate must be between 0% and 50%.',
        }),
      );
      return;
    }
    if (parsed.data.rateBasisPoints === currentRateBasisPoints) {
      setServerError(
        t('admin:commission.dialog.edit_rate.error.rate_unchanged', {
          defaultValue: 'Rate unchanged — no save required.',
        }),
      );
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(parsed.data);
      onClose();
    } catch (err) {
      const code =
        err !== null && typeof err === 'object' && 'code' in err
          ? (err as { code: string }).code
          : null;
      if (code === 'COMMISSION_RATE_NO_CHANGE') {
        setServerError(
          t('admin:commission.dialog.edit_rate.error.rate_unchanged', {
            defaultValue: 'Rate unchanged — no save required.',
          }),
        );
      } else {
        setServerError(
          t('admin:commission.toast.error_generic', {
            defaultValue: 'Something went wrong. Please try again.',
          }),
        );
      }
    } finally {
      setSubmitting(false);
    }
  });

  const disableSubmit =
    submitting ||
    !isValid ||
    unchanged ||
    !reason ||
    reason.trim().length < 1 ||
    reason.length > 500 ||
    rateBasisPoints < MIN_RATE_BASIS_POINTS ||
    rateBasisPoints > MAX_RATE_BASIS_POINTS;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:commission.dialog.edit_rate.title', {
        defaultValue: 'Change commission rate',
      })}
      description={t('admin:commission.dialog.edit_rate.description', {
        defaultValue:
          'The new rate applies forward only. Past commission records keep their snapshotted rate.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="edit-rate-cancel"
          >
            {t('admin:commission.dialog.edit_rate.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={disableSubmit}
            data-testid="edit-rate-confirm"
          >
            {t('admin:commission.dialog.edit_rate.confirm', {
              defaultValue: 'Apply rate change',
            })}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        data-testid="edit-rate-dialog"
        data-venue-id={venueId}
        onSubmit={submit}
      >
        <p className="text-[12px] text-ink-3">
          {/* CST-010 — dedicated key (the borrowed kpis.commission rendered
              "Commission: <venueName>"). */}
          {t('admin:commission.dialog.edit_rate.venue_label', {
            defaultValue: 'Venue',
          })}
          : <span className="font-semibold text-ink">{venueName}</span>
        </p>

        <div data-testid="edit-rate-forward-only-banner">
          <Banner
            tone="info"
            title={t('admin:commission.dialog.edit_rate.banner.forward_only_title', {
              defaultValue: 'Forward-only change',
            })}
          >
            {t('admin:commission.dialog.edit_rate.banner.forward_only_body', {
              defaultValue:
                'Existing records keep the previous rate. New accruals from the moment of save will use the new rate.',
            })}
          </Banner>
        </div>

        <FormField
          label={t('admin:commission.dialog.edit_rate.rate_label', {
            defaultValue: 'New rate (%)',
          })}
          helper={t('admin:commission.dialog.edit_rate.rate_helper', {
            defaultValue: 'Between 0% and 50%. Two decimals.',
          })}
          error={
            errors.ratePercent ? (errors.ratePercent.message as string | undefined) : undefined
          }
          required
        >
          <NumberInput
            value={ratePercent}
            min={MIN_PERCENT}
            max={MAX_PERCENT}
            step={0.01}
            data-testid="edit-rate-percent-input"
            aria-label={t('admin:commission.dialog.edit_rate.rate_label', {
              defaultValue: 'New rate (%)',
            })}
            onChange={(v) => setValue('ratePercent', Number(v), { shouldValidate: true })}
          />
        </FormField>

        <FormField
          label={t('admin:commission.dialog.edit_rate.reason_label', {
            defaultValue: 'Reason (audit-logged)',
          })}
          helper={t('admin:commission.dialog.edit_rate.reason_helper', {
            defaultValue: 'Visible to the finance team in the audit log.',
          })}
          required
        >
          <Textarea
            rows={3}
            maxLength={500}
            data-testid="edit-rate-reason"
            {...register('reason', {
              required: true,
              minLength: 1,
              maxLength: 500,
              setValueAs: (v: string) => v ?? '',
            })}
          />
        </FormField>

        {serverError ? (
          <div data-testid="edit-rate-server-error">
            <Banner tone="danger">{serverError}</Banner>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}

export default EditRateDialog;
